import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQueryWithReauth } from '@/services';
import { API_BUSINESS_URL } from '@/utils';

import { CommerceGroup, NewCommerceGroup } from '../models';

const commerceGroupsService = createApi({
  reducerPath: 'commerceGroupsService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}/commerce-group`),
  tagTypes: ['CommerceGroups'] as const,
  endpoints: (builder) => ({
    getCommerceGroups: builder.query<
      CommerceGroup[],
      { limit?: number; offset?: number; name?: string } | void
    >({
      query: (args) => ({
        url: '',
        params: {
          limit: args?.limit ?? 200,
          offset: args?.offset ?? 0,
          ...(args?.name &&
            args.name.trim() !== '' && { name: args.name.trim() }),
        },
      }),
      transformResponse: (response: {
        items?: CommerceGroup[];
        count?: number;
      }): CommerceGroup[] =>
        (response?.items ?? [])
          .map((group) => ({
            id: String(group.id ?? ''),
            name: String(group.name ?? ''),
          }))
          .filter((group) => group.id !== '' && group.name !== ''),
      providesTags: ['CommerceGroups'],
    }),
    createCommerceGroup: builder.mutation<CommerceGroup, NewCommerceGroup>({
      query: (body) => ({
        url: '',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CommerceGroups'],
    }),
    updateCommerceGroup: builder.mutation<
      CommerceGroup,
      { commerceGroupId: string; data: NewCommerceGroup }
    >({
      query: ({ commerceGroupId, data }) => ({
        url: `/${commerceGroupId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['CommerceGroups'],
    }),
    deleteCommerceGroup: builder.mutation<void, string>({
      query: (commerceGroupId) => ({
        url: `/${commerceGroupId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CommerceGroups'],
    }),
  }),
});

export default commerceGroupsService;
export const {
  useGetCommerceGroupsQuery,
  useCreateCommerceGroupMutation,
  useUpdateCommerceGroupMutation,
  useDeleteCommerceGroupMutation,
} = commerceGroupsService;
